// The accent roster — which brand tokens may be a slide or section accent.
//
// CURATED, NEVER DERIVED. It would be tempting to scan tokens.js for its
// uppercase exports (validate-slide-imports.ts does exactly that for the import
// gate), but that set also holds BG, BG_*, FG, FG_*, LINE, LINE_2, SANS, MONO
// and RED_NEG. RED_NEG in particular is a semantic red for negative deltas and
// must never land in an accent rotation, so the roster is written out by hand
// and every consumer reads it from here.
//
// This module is a LEAF: it imports nothing, so it can be pulled into the
// server, the client bundle and the build pipeline without adding a cycle.
//
// The accents.d.ts mirror this used to need is GONE. It declared the same nine
// names a second time and warned that "dropping one from the .js without
// dropping it here silently re-admits a dead name" — a real hazard it could
// describe but not prevent. The unions below are now DERIVED from the arrays
// (`(typeof ACCENT_TOKENS)[number]`), so the roster is stated exactly once and
// that failure mode no longer exists.
//
// ORDER MATTERS — append, never insert. pipeline/library-export.ts's
// ACCENT_CYCLE and pipeline/plan-structure.ts's "first unused colour" pick both
// depend on position, and their tests assert specific indices.

/** Accents offered in every picker and every AI schema. */
export const ACCENT_TOKENS = [
  'ORANGE',
  'CYAN',
  'GREEN',
  'PINK',
  'PURPLE',
  'LEMON',
  'BLOSSOM',
  'BLUSH',
  'SCARLET',
] as const;

/** Accepted in existing deck source, never offered in a picker or schema.
 *  YELLOW resolves to the same lime as GREEN, so offering both would look like
 *  a duplicate swatch. */
export const LEGACY_ACCENT_TOKENS = ['YELLOW'] as const;

/** Every name that is a legal accent, offered or not. Validation reads this;
 *  pickers and schemas read ACCENT_TOKENS. */
export const ALL_ACCENT_TOKENS = [...ACCENT_TOKENS, ...LEGACY_ACCENT_TOKENS] as const;

/** Accents offered in every picker and every AI schema. */
export type AccentToken = (typeof ACCENT_TOKENS)[number];

/** Accepted in existing deck source, never offered in a picker or schema. */
export type LegacyAccentToken = (typeof LEGACY_ACCENT_TOKENS)[number];

/** Every name that is a legal accent, offered or not. THE narrow type of the
 *  codebase: an invalid accent becomes a compile error in every slide, codec
 *  and schema that takes one, rather than an orange slide nobody asked for. */
export type AnyAccentToken = AccentToken | LegacyAccentToken;
